/**
 * ReportMLMetrics.js — ML model performance metrics
 */
import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, CartesianGrid } from "recharts";
import { SEVERITY_COLORS } from "../reportUtils";

export default function ReportMLMetrics({ mlResult, stats, theme, SEVERITY_COLORS: SC }) {
  if (!mlResult) return (
    <div className="report-section" style={{ marginBottom: 24 }}>
      <SectionTitle color="#00695C">ML Model Metrics</SectionTitle>
      <div style={{ padding: 20, background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, fontFamily: "monospace", fontSize: 12, color: theme.colors.textMuted, textAlign: "center" }}>
        No ML analysis results — run ANALYZE on the Dashboard first, then return to Reports
      </div>
    </div>
  );

  const metrics = mlResult.metrics;
  const radarData = metrics ? [
    { metric: "Accuracy",  value: metrics.accuracy  },
    { metric: "Precision", value: metrics.precision },
    { metric: "Recall",    value: metrics.recall    },
    { metric: "F1 Score",  value: metrics.f1        },
  ] : [];

  const barData = metrics ? [
    { name: "Accuracy",  value: metrics.accuracy,  color: theme.colors.accent },
    { name: "Precision", value: metrics.precision,  color: "#2E7D32" },
    { name: "Recall",    value: metrics.recall,    color: "#F57F17" },
    { name: "F1 Score",  value: metrics.f1,        color: theme.colors.accent },
  ] : [];

  return (
    <div className="report-section page-break" style={{ marginBottom: 24 }}>
      <SectionTitle color="#00695C">ML Model Intelligence</SectionTitle>

      {/* Model info strip */}
      <div style={{
        background: "rgba(0,105,92,0.08)", border: "1px solid rgba(0,105,92,0.25)",
        borderRadius: 8, padding: "12px 20px", marginBottom: 16,
        display: "flex", gap: 32, flexWrap: "wrap", fontFamily: "monospace", fontSize: 12
      }}>
        <span style={{ color: "#00695C", fontWeight: 700 }}>MODEL: {(mlResult.model||"").replace(/_/g," ").toUpperCase()}</span>
        <span style={{ color: theme.colors.textSecondary }}>Type: <strong>{mlResult.supervised ? "Supervised" : "Unsupervised"}</strong></span>
        <span style={{ color: theme.colors.textSecondary }}>Total Records: <strong>{(mlResult.total_records||0).toLocaleString()}</strong></span>
        <span style={{ color: SC.critical }}>Anomalies: <strong>{(mlResult.anomalies||0).toLocaleString()}</strong></span>
        <span style={{ color: SC.high }}>Anomaly Rate: <strong>{mlResult.anomaly_rate}%</strong></span>
        {mlResult.vendor && mlResult.vendor !== "generic" && (
          <span style={{ color: theme.colors.accent }}>Vendor: <strong>{mlResult.vendor.replace(/_/g," ").toUpperCase()}</strong></span>
        )}
      </div>

      {metrics ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {/* Metric tiles */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {barData.map(({ name, value, color }) => (
              <div key={name} style={{ background: `${color}12`, border: `1px solid ${color}33`, borderRadius: 10, padding: "16px 20px", textAlign: "center" }}>
                <div style={{ fontFamily: "monospace", fontSize: 32, fontWeight: 900, color }}>{value}%</div>
                <div style={{ fontFamily: "monospace", fontSize: 10, color: theme.colors.textMuted, letterSpacing: 1, marginTop: 4 }}>{name.toUpperCase()}</div>
                <div style={{ fontFamily: "monospace", fontSize: 9, color: theme.colors.textMuted, marginTop: 2 }}>
                  {value >= 95 ? "Excellent" : value >= 90 ? "Good" : value >= 80 ? "Acceptable" : "Review needed"}
                </div>
              </div>
            ))}

            {/* Train/test split */}
            {metrics.train_size && (
              <div style={{ gridColumn: "1/-1", background: theme.colors.tableHeader, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: "12px 16px" }}>
                <div style={{ fontFamily: "monospace", fontSize: 10, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 8 }}>TRAIN / TEST SPLIT</div>
                <div style={{ display: "flex", gap: 20 }}>
                  <span style={{ fontFamily: "monospace", fontSize: 13, color: "#2E7D32" }}>Train: <strong>{metrics.train_size?.toLocaleString()}</strong> ({Math.round(metrics.train_size/(metrics.train_size+metrics.test_size)*100)}%)</span>
                  <span style={{ fontFamily: "monospace", fontSize: 13, color: theme.colors.accent }}>Test: <strong>{metrics.test_size?.toLocaleString()}</strong> ({Math.round(metrics.test_size/(metrics.train_size+metrics.test_size)*100)}%)</span>
                  <span style={{ fontFamily: "monospace", fontSize: 12, color: theme.colors.textMuted }}>Stratified · Random seed: 42</span>
                </div>
              </div>
            )}
          </div>

          {/* Bar chart */}
          <div className="report-card chart-container" style={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: 20 }}>
            <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1, marginBottom: 12 }}>PERFORMANCE METRICS</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={barData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.cardBorder} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: theme.colors.textSecondary }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: theme.colors.textMuted }} />
                <Tooltip contentStyle={{ background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, fontSize: 12 }} formatter={(v) => [`${v}%`]} />
                <Bar dataKey="value" radius={[4,4,0,0]}>
                  {barData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* IEEE paper context */}
            <div style={{ marginTop: 12, padding: "10px 12px", background: theme.colors.tableHeader, borderRadius: 6, fontFamily: "monospace", fontSize: 10, color: theme.colors.textMuted }}>
              IEEE Paper Reference: ROSpace 100% · TON_IoT 91.7% · UNSW-NB15 99.4% · CIC-IDS2017 100%
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: 20, background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, fontFamily: "monospace", fontSize: 12, color: theme.colors.textMuted }}>
          Unsupervised model (Isolation Forest) — supervised metrics not available.
          Anomalies detected: <strong style={{ color: SC.critical }}>{(mlResult.anomalies||0).toLocaleString()}</strong> at rate of <strong style={{ color: SC.high }}>{mlResult.anomaly_rate}%</strong>.
          Run with Random Forest or Logistic Regression on a labeled dataset for full metrics.
        </div>
      )}

      {/* Features used */}
      {mlResult.features_used?.length > 0 && (
        <div style={{ marginTop: 16, background: theme.colors.card, border: `1px solid ${theme.colors.cardBorder}`, borderRadius: 10, padding: 16 }}>
          <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textLabel, letterSpacing: 1, marginBottom: 10, fontWeight: 700 }}>FEATURES USED IN ANALYSIS ({mlResult.features_used.length})</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {mlResult.features_used.map((f, i) => (
              <span key={i} style={{ padding: "3px 10px", background: "rgba(0,150,136,0.15)", border: "1px solid rgba(0,150,136,0.4)", borderRadius: 4, fontFamily: "monospace", fontSize: 11, color: "#4DB6AC", fontWeight: 600 }}>{f}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const SectionTitle = ({ children, color }) => (
  <div style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 900, color, letterSpacing: 2, textTransform: "uppercase", borderLeft: `4px solid ${color}`, paddingLeft: 12, marginBottom: 16 }}>{children}</div>
);
